import React, { useEffect, useMemo, useState } from "react";
import { useVerifyEmailChange } from "../../hooks/user";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Card, CardContent, Container, Fade, Typography, Alert, Button, useTheme, CircularProgress } from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { USER_PROFILE_ROUTE_PATH, PROFILE_TAB_QUERY_PARAM, PROFILE_TAB_SECURITY } from "../../utils/constants/AppConstants";
import { ApiError } from "../../error/errorTypes";

type VerifyType = "old" | "new";

const VerifyEmailChangePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = useMemo(() => params.get("token") || "", [params]);
  const verifyType = useMemo<VerifyType | null>(() => {
    const t = (params.get("type") || "").toLowerCase();
    if (t === "old" || t === "new") return t;
    return null;
  }, [params]);

  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{
    message: string;
    oldEmailVerified: boolean;
    newEmailVerified: boolean;
    changeCompleted: boolean;
  } | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const { mutate: verifyEmailChange, isPending: loading } = useVerifyEmailChange();

  useEffect(() => {
    if (!token || !verifyType) {
      setError("Invalid verification link. Missing token or type.");
      return;
    }
    
    verifyEmailChange(
      { token, type: verifyType },
      {
        onSuccess: (result) => {
          if (result) {
            setStatus(result);
          } else {
            setError("Verification failed. Please try again later.");
          }
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const details = err.status ? ` (HTTP ${err.status})` : "";
            setError((err.userMessage || err.message) + details);
          } else if (err instanceof Error) {
            setError(err.message);
          } else {
            setError("Verification failed. The link may be invalid or expired.");
          }
        }
      }
    );
  }, [token, verifyType, verifyEmailChange]);

  // Redirect to profile automatically after a short delay once we have a successful status
  useEffect(() => {
    if (!status) return;
    // Start a 4s countdown
    setRedirectCountdown(4);
    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(interval);
          navigate(`/${USER_PROFILE_ROUTE_PATH}?${PROFILE_TAB_QUERY_PARAM}=${PROFILE_TAB_SECURITY}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4}}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={700}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`}}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"}}
              >
                Verify email change
              </Typography>

              {loading ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <ErrorOutline sx={{ mr: 1, verticalAlign: "middle" }} />
                    {error}
                  </Alert>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The verification link may be expired or invalid. Please check your email for a new link or manage your email change from your profile.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/${USER_PROFILE_ROUTE_PATH}?${PROFILE_TAB_QUERY_PARAM}=${PROFILE_TAB_SECURITY}`)}
                    >
                      Go to Profile
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/")}
                    >
                      Back to Home
                    </Button>
                  </Box>
                </Box>
              ) : status ? (
                <Box sx={{ textAlign: "center" }}>
                  <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 48, mb: 1 }} />
                  <Typography variant="h5" gutterBottom>
                    Email verified
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {verifyType === "old"
                      ? "Your current email has been verified. Please also verify your new email from the link we sent."
                      : "Your new email has been verified. We’ll finish up in your profile."}
                  </Typography>
                  {redirectCountdown !== null && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You will be redirected to your profile automatically.
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={() => navigate(`/${USER_PROFILE_ROUTE_PATH}?${PROFILE_TAB_QUERY_PARAM}=${PROFILE_TAB_SECURITY}`)}>
                      {redirectCountdown !== null && redirectCountdown > 0
                        ? `Go to profile (${redirectCountdown}s)`
                        : "Go to profile"}
                    </Button>
                  </Box>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailChangePage;


